import { z } from 'zod';
import { LOGO_MAX_BYTES } from './signature';

/**
 * The image a print template carries inside itself: its letterhead mark.
 *
 * Signatures are never template data — they are read from the `User` and
 * `Consultant` rows (signature.ts). The ceiling is shared with the lab
 * profile's logo, so raising it is one edit.
 */

/**
 * The letterhead mark.
 *
 * `null` means the bundled NDC mark. Anything else is a data URL for an image
 * the lab uploaded, stored inline in the template.
 *
 * Inline rather than a file-upload endpoint on purpose: a template is then a
 * single self-contained JSON document that can be exported, copied between
 * environments and rolled back with no orphaned blobs to garbage-collect. The
 * 512 KB ceiling is roughly a 1000 px PNG, well past what a 30 mm mark needs at
 * print resolution.
 */
export const printLogoSchema = z.object({
  enabled: z.boolean().default(true),
  data_url: z
    .string()
    .max(LOGO_MAX_BYTES, 'Logo must be under 512 KB')
    .regex(/^data:image\/(png|jpeg|gif);base64,[A-Za-z0-9+/=]+$/, 'Upload a PNG, JPEG or GIF')
    .nullable()
    .default(null),
  width: z.coerce.number().min(0).max(120).default(30),
  height: z.coerce.number().min(0).max(120).default(26),
});
export type PrintLogo = z.infer<typeof printLogoSchema>;
